import React, { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { Play, Pause, Volume2, X } from 'lucide-react';

const AudioPlayer = ({ audioUrl, productName, onClose }) => {
    const waveformRef = useRef(null);
    const wavesurfer = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [duration, setDuration] = useState('0:00');
    const [currentTime, setCurrentTime] = useState('0:00');

    useEffect(() => {
        if (waveformRef.current && audioUrl) {
            wavesurfer.current = WaveSurfer.create({
                container: waveformRef.current,
                waveColor: 'rgba(163, 163, 163, 0.5)',
                progressColor: '#6366F1',
                cursorColor: '#6366F1',
                barWidth: 2,
                barGap: 1,
                height: 48,
                responsive: true,
                normalize: true
            });

            wavesurfer.current.load(audioUrl);

            wavesurfer.current.on('ready', () => {
                setDuration(formatTime(wavesurfer.current.getDuration()));
            });

            wavesurfer.current.on('audioprocess', () => {
                setCurrentTime(formatTime(wavesurfer.current.getCurrentTime()));
            });

            wavesurfer.current.on('finish', () => {
                setIsPlaying(false);
            });

            return () => {
                if (wavesurfer.current) {
                    wavesurfer.current.destroy();
                }
            };
        }
    }, [audioUrl]);

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
    };

    const togglePlayPause = () => {
        if (wavesurfer.current) {
            wavesurfer.current.playPause();
            setIsPlaying(!isPlaying);
        }
    };

    if (!audioUrl) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 glass-effect p-4 z-50 flex items-center gap-6" data-testid="audio-player">
            <button 
                onClick={togglePlayPause}
                className="p-3 bg-primary rounded-full hover:bg-primary/90 transition-colors flex-shrink-0"
                data-testid="play-pause-button"
            >
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </button>

            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium mb-1 truncate">{productName || 'Playing'}</div>
                <div ref={waveformRef} className="w-full" />
            </div>

            <div className="flex items-center gap-4 flex-shrink-0">
                <div className="text-xs font-mono text-muted-foreground" data-testid="audio-time">
                    {currentTime} / {duration}
                </div>
                <Volume2 className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                <button 
                    onClick={onClose}
                    className="p-2 hover:bg-white/10 rounded-full transition-colors"
                    data-testid="close-player-button"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

export default AudioPlayer;
